<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import ReportsAPI from 'dashboard/api/reports';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const isLoading = ref(false);
const errorText = ref('');

const summary = ref({
  ai_conversations: 0,
  handoff_conversations: 0,
  resolved_conversations: 0,
  stuck_pending: 0,
  stuck_minutes: 15,
});

const jobErrors = ref({ total: 0, top_by_name: [], top_by_inbox: [] });
const opsActions = ref({ total: 0, by_type: [] });
const doubleReply = ref({ window_seconds: 120, sample_count: 0, samples: [] });

const hasDateRange = computed(() => props.filters?.from && props.filters?.to);

const fetchData = async () => {
  if (!hasDateRange.value) return;
  isLoading.value = true;
  errorText.value = '';
  try {
    const response = await ReportsAPI.getBotAiInsights({
      from: props.filters.from,
      to: props.filters.to,
      stuckMinutes: 15,
      doubleReplyWindowSeconds: 120,
    });
    summary.value = response.data.summary || summary.value;
    jobErrors.value = response.data.job_errors || jobErrors.value;
    opsActions.value = response.data.ops_actions || opsActions.value;
    doubleReply.value =
      response.data.double_reply_sentinel || doubleReply.value;
  } catch (e) {
    errorText.value = e?.message || 'Failed to load AI insights';
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.filters, fetchData, { deep: true });
onMounted(fetchData);
</script>

<template>
  <div
    class="mx-0 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
  >
    <div class="flex items-start justify-between gap-4">
      <div class="flex flex-col gap-1">
        <div class="text-base font-semibold text-slate-800 dark:text-white">
          {{ $t('BOT_REPORTS.INSIGHTS.HEADER') }}
        </div>
        <div class="text-sm text-slate-600 dark:text-white/60">
          {{ $t('BOT_REPORTS.INSIGHTS.DESCRIPTION') }}
        </div>
      </div>
    </div>

    <div
      v-if="!hasDateRange"
      class="mt-4 text-sm text-slate-600 dark:text-white/60"
    >
      {{ $t('BOT_REPORTS.INSIGHTS.EMPTY_STATE') }}
    </div>

    <div v-else class="mt-4">
      <div v-if="isLoading" class="text-sm text-slate-600 dark:text-white/60">
        {{ $t('BOT_REPORTS.INSIGHTS.LOADING') }}
      </div>
      <div v-else-if="errorText" class="text-sm text-red-600">
        {{ errorText }}
      </div>
      <div v-else class="flex flex-col gap-4">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div class="rounded-lg border border-white/5 px-4 py-3">
            <div class="text-xs text-slate-600 dark:text-white/60">
              {{ $t('BOT_REPORTS.INSIGHTS.SUMMARY.AI_CONVERSATIONS') }}
            </div>
            <div class="text-lg font-semibold text-slate-800 dark:text-white">
              {{ summary.ai_conversations }}
            </div>
          </div>
          <div class="rounded-lg border border-white/5 px-4 py-3">
            <div class="text-xs text-slate-600 dark:text-white/60">
              {{ $t('BOT_REPORTS.INSIGHTS.SUMMARY.HANDOFF') }}
            </div>
            <div class="text-lg font-semibold text-slate-800 dark:text-white">
              {{ summary.handoff_conversations }}
            </div>
          </div>
          <div class="rounded-lg border border-white/5 px-4 py-3">
            <div class="text-xs text-slate-600 dark:text-white/60">
              {{ $t('BOT_REPORTS.INSIGHTS.SUMMARY.RESOLVED') }}
            </div>
            <div class="text-lg font-semibold text-slate-800 dark:text-white">
              {{ summary.resolved_conversations }}
            </div>
          </div>
          <div class="rounded-lg border border-white/5 px-4 py-3">
            <div class="text-xs text-slate-600 dark:text-white/60">
              {{
                $t('BOT_REPORTS.INSIGHTS.SUMMARY.STUCK', {
                  minutes: summary.stuck_minutes,
                })
              }}
            </div>
            <div class="text-lg font-semibold text-slate-800 dark:text-white">
              {{ summary.stuck_pending }}
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div class="rounded-lg border border-white/5 px-4 py-3">
            <div class="text-sm font-semibold text-slate-800 dark:text-white">
              {{ $t('BOT_REPORTS.INSIGHTS.JOB_ERRORS.HEADER') }}
            </div>
            <div class="text-sm text-slate-600 dark:text-white/60 mt-1">
              {{
                $t('BOT_REPORTS.INSIGHTS.JOB_ERRORS.TOTAL', {
                  count: jobErrors.total || 0,
                })
              }}
            </div>

            <div class="mt-3">
              <div class="text-xs text-slate-600 dark:text-white/60 mb-1">
                {{ $t('BOT_REPORTS.INSIGHTS.JOB_ERRORS.TOP_BY_NAME') }}
              </div>
              <ul class="text-sm text-slate-800 dark:text-white space-y-1">
                <li v-for="row in jobErrors.top_by_name || []" :key="row.name">
                  <span class="font-mono">{{ row.name }}</span> —
                  {{ row.count }}
                </li>
                <li
                  v-if="!(jobErrors.top_by_name || []).length"
                  class="text-slate-600 dark:text-white/60"
                >
                  {{ $t('BOT_REPORTS.INSIGHTS.NONE') }}
                </li>
              </ul>
            </div>
          </div>

          <div class="rounded-lg border border-white/5 px-4 py-3">
            <div class="text-sm font-semibold text-slate-800 dark:text-white">
              {{ $t('BOT_REPORTS.INSIGHTS.OPS.HEADER') }}
            </div>
            <div class="text-sm text-slate-600 dark:text-white/60 mt-1">
              {{
                $t('BOT_REPORTS.INSIGHTS.OPS.TOTAL', {
                  count: opsActions.total || 0,
                })
              }}
            </div>
            <ul class="text-sm text-slate-800 dark:text-white space-y-1 mt-3">
              <li
                v-for="row in opsActions.by_type || []"
                :key="row.decision_type"
              >
                <span class="font-mono">{{ row.decision_type }}</span> —
                {{ row.count }}
              </li>
              <li
                v-if="!(opsActions.by_type || []).length"
                class="text-slate-600 dark:text-white/60"
              >
                {{ $t('BOT_REPORTS.INSIGHTS.NONE') }}
              </li>
            </ul>
          </div>
        </div>

        <div class="rounded-lg border border-white/5 px-4 py-3">
          <div class="text-sm font-semibold text-slate-800 dark:text-white">
            {{ $t('BOT_REPORTS.INSIGHTS.DOUBLE_REPLY.HEADER') }}
          </div>
          <div class="text-sm text-slate-600 dark:text-white/60 mt-1">
            {{
              $t('BOT_REPORTS.INSIGHTS.DOUBLE_REPLY.SUMMARY', {
                seconds: doubleReply.window_seconds,
                count: doubleReply.sample_count,
              })
            }}
          </div>
          <div v-if="(doubleReply.samples || []).length" class="mt-2 text-sm">
            <div
              v-for="row in doubleReply.samples"
              :key="row.incoming_message_id"
              class="text-slate-800 dark:text-white"
            >
              #{{ row.conversation_id }} — incoming
              {{ row.incoming_message_id }} ({{ row.incoming_at }})
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
